import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Globe, Loader2, ShieldCheck, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { I18nProvider, useI18n, LOCALES } from "@/lib/i18n";
import { ContactStep, validateContact } from "@/components/intake/ContactStep";
import { OptionList, StepHeader, Field } from "@/components/intake/Primitives";
import { SupervisorAssignStep } from "@/components/intake/SupervisorAssignStep";

const DRAFT_KEY = "emerge_intake_draft_v1";
const SUPERVISOR_DRAFT_KEY = "emerge_supervisor_intake_draft_v1";
export const SERVICE_OPTIONS = ["psychology", "speech_language", "psychomotor", "orthopedagogy", "assessment", "other", "not_sure"];
export const ASSESSMENT_OPTIONS = ["autism", "adhd", "cognitive_iq", "psychological", "speech_language", "psychomotor", "educational", "other", "not_sure"];
export const REFERRAL_OPTIONS = ["doctor_psychiatrist", "psychologist", "school", "speech_therapist", "other_therapist", "ngo", "friend_family", "social_media", "self_referral", "other"];
export const TIME_OPTIONS = ["morning", "afternoon", "evening"];
// "none" is a UI-only sentinel; it is stored as an empty preferred_contact_time.
const TIME_OPTIONS_WITH_NONE = [...TIME_OPTIONS, "none"];

const EMPTY = {
  language: "",
  beneficiary_name: "",
  dobMode: "dob",
  dob: "",
  age: "",
  contact_name: "",
  relationship: "",
  relationship_other: "",
  phone: "",
  service: "",
  service_other: "",
  assessment_type: "",
  assessment_other: "",
  referral_source: "",
  referral_other: "",
  preferred_contact_time: "",
  preferred_time_specific: "",
  website: "",
};

function loadDraft(key = DRAFT_KEY) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

function WizardInner({ supervisorMode = false }) {
  const { t, locale, setLocale, isRTL } = useI18n();
  const [searchParams] = useSearchParams();
  const trackingToken = searchParams.get("t") || "";
  const draftKey = supervisorMode ? SUPERVISOR_DRAFT_KEY : DRAFT_KEY;

  const [draft] = useState(() => loadDraft(draftKey));
  const [data, setData] = useState(() => ({ ...EMPTY, ...(draft?.data || {}) }));
  const [stepIndex, setStepIndex] = useState(() => (draft?.stepIndex && draft.data?.language ? draft.stepIndex : 0));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [restored, setRestored] = useState(() => !!(draft?.data?.language && draft?.stepIndex > 0));
  const [booking, setBooking] = useState({
    admin_id: "", date: "", start: "", duration_minutes: 45, durationMode: 45,
    type: "intake_call", confirmed: true,
  });

  useEffect(() => {
    if (data.language && LOCALES[data.language] && data.language !== locale) setLocale(data.language);
  }, [data.language, locale, setLocale]);

  // Steps: dynamic (assessment only when service === assessment)
  const steps = useMemo(() => {
    const s = ["language", "contact", "service"];
    if (data.service === "assessment") s.push("assessment");
    s.push("referral", "time");
    if (supervisorMode) s.push("assign");
    return s;
  }, [data.service, supervisorMode]);

  const current = result ? "done" : steps[Math.min(stepIndex, steps.length - 1)];
  const totalSteps = steps.length;
  const progress = result ? 100 : Math.round((stepIndex / totalSteps) * 100);

  // Autosave
  useEffect(() => {
    if (result) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify({ data, stepIndex, savedAt: Date.now() }));
    } catch {
      /* ignore quota errors */
    }
  }, [data, stepIndex, result, draftKey]);

  const update = useCallback((patch) => {
    setData((d) => ({ ...d, ...patch }));
    setErrors((e) => {
      const next = { ...e };
      Object.keys(patch).forEach((k) => delete next[k]);
      if (patch.dobMode) {
        delete next.dob;
        delete next.age;
      }
      if (patch.relationship) delete next.relationship_other;
      if (patch.service) delete next.service_other;
      if (patch.assessment_type) delete next.assessment_other;
      if (patch.referral_source) delete next.referral_other;
      return next;
    });
  }, []);

  const validateStep = () => {
    const e = {};
    if (current === "contact") Object.assign(e, validateContact(data, t));
    if (current === "service") {
      if (!data.service) e.service = t("validation.selectOne");
      else if (data.service === "other" && !(data.service_other || "").trim()) e.service_other = t("validation.specifyOther");
    }
    if (current === "assessment") {
      if (!data.assessment_type) e.assessment_type = t("validation.selectOne");
      else if (data.assessment_type === "other" && !(data.assessment_other || "").trim()) e.assessment_other = t("validation.specifyOther");
    }
    if (current === "referral") {
      if (!data.referral_source) e.referral_source = t("validation.selectOne");
      else if (data.referral_source === "other" && !(data.referral_other || "").trim()) e.referral_other = t("validation.specifyOther");
    }
    if (current === "time") {
      if (data.preferred_time_specific && !/^([01]\d|2[0-3]):[0-5]\d$/.test(data.preferred_time_specific)) e.preferred_time_specific = t("validation.timeFormat");
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goNext = () => {
    if (!validateStep()) return;
    if (stepIndex < totalSteps - 1) {
      setStepIndex((i) => i + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      submit();
    }
  };

  const goBack = () => {
    setErrors({});
    setStepIndex((i) => Math.max(0, i - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const chooseLanguage = (lang) => {
    update({ language: lang });
    setLocale(lang);
    setStepIndex(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        language: data.language,
        beneficiary_name: data.beneficiary_name.trim(),
        dob: data.dobMode === "dob" ? data.dob : null,
        age: data.dobMode === "age" ? Number(data.age) : null,
        contact_name: data.contact_name.trim(),
        relationship: data.relationship,
        relationship_other: data.relationship === "other" ? data.relationship_other.trim() : null,
        phone: data.phone,
        service: data.service,
        service_other: data.service === "other" ? data.service_other.trim() : null,
        assessment_type: data.service === "assessment" ? data.assessment_type : null,
        assessment_other: data.service === "assessment" && data.assessment_type === "other" ? data.assessment_other.trim() : null,
        referral_source: data.referral_source,
        referral_other: data.referral_source === "other" ? data.referral_other.trim() : null,
        preferred_contact_time: data.preferred_contact_time || null,
        preferred_time_specific: data.preferred_time_specific || null,
        tracking_token: trackingToken || null,
        website: data.website || "",
      };
      let res;
      if (supervisorMode) {
        const booked = booking.admin_id && booking.start;
        res = await api.post("/supervisor-intake", {
          ...payload,
          admin_id: booked ? booking.admin_id : null,
          start: booked ? booking.start : null,
          duration_minutes: booking.duration_minutes,
          type: booking.type,
          confirmed: booking.confirmed !== false,
        });
      } else {
        res = await api.post("/public/intake", payload);
      }
      setResult(res.data);
      localStorage.removeItem(draftKey);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const status = err?.response?.status;
      toast.error(status === 429 ? t("common.errorRateLimit") : errorMessage(err, t("common.errorGeneric")));
    } finally {
      setSubmitting(false);
    }
  };

  const startOver = () => {
    localStorage.removeItem(draftKey);
    setData({ ...EMPTY });
    setResult(null);
    setErrors({});
    setStepIndex(0);
    setRestored(false);
  };

  const stepNumber = stepIndex + 1;
  const BackIcon = isRTL ? ArrowRight : ArrowLeft;
  const NextIcon = isRTL ? ArrowLeft : ArrowRight;

  return (
    <div className="min-h-dvh bg-background" data-testid="intake-page">
      {/* Header band */}
      <header className="sticky top-0 z-30 bg-[hsl(var(--app-navy-deep))] text-white">
        <div className="relative mx-auto w-full max-w-[560px] px-4 pb-3 pt-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <img src="/brand/emerge-logo-white.png" alt="EMERGE" className="h-7 w-auto" />
            </div>
            <div className="flex items-center gap-2">
              {current !== "language" && current !== "done" && (
                <button
                  type="button"
                  data-testid="intake-change-language-button"
                  onClick={() => {
                    setStepIndex(0);
                    setErrors({});
                  }}
                  className="inline-flex h-9 items-center gap-1.5 rounded-full border border-white/25 px-3 text-xs font-medium text-white/90 hover:bg-white/10"
                >
                  <Globe className="h-3.5 w-3.5" />
                  {LOCALES[locale]?.languageName}
                </button>
              )}
            </div>
          </div>
          <p className="mt-1 text-[11px] tracking-wide text-white/70">{t("brand.tagline")}</p>
          <div className="mt-3">
            <div className="progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
              <div className="progress-fill" style={{ width: `${Math.max(progress, 4)}%` }} />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-white/80">
              <span data-testid="intake-progress-step">{result ? t("steps.done") : t("common.stepOf", { current: stepNumber, total: totalSteps })}</span>
              <span className="inline-flex items-center gap-1">
                <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
                {t("common.answered", { percent: progress })}
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[560px] px-4 pb-32 pt-6">
        {restored && !result && current !== "language" && (
          <div className="mb-5 flex items-center justify-between gap-3 rounded-[10px] border border-[hsl(var(--app-teal))]/30 bg-[hsl(var(--app-teal))]/8 px-4 py-3 text-sm text-foreground" role="status">
            <span>{t("common.resumeDraft")}</span>
            <button type="button" onClick={startOver} data-testid="intake-start-over-button" className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-primary hover:underline">
              <RotateCcw className="h-3.5 w-3.5" /> {t("common.startOver")}
            </button>
          </div>
        )}

        {/* Honeypot */}
        <div className="sr-only" aria-hidden="true">
          <label>
            Website <input type="text" name="website" tabIndex={-1} autoComplete="off" value={data.website} onChange={(e) => update({ website: e.target.value })} />
          </label>
        </div>

        {current === "language" && (
          <div className="step-enter">
            <StepHeader number={1} title={t("step1.title")} subtitle={t("step1.hint")} />
            <div className="grid gap-3">
              {[
                { code: "ar", label: "العربية", sub: "Arabic" },
                { code: "en", label: "English", sub: "English" },
                { code: "fr", label: "Français", sub: "French" },
              ].map((l) => (
                <button
                  key={l.code}
                  type="button"
                  data-testid={`intake-language-${l.code}-card`}
                  onClick={() => chooseLanguage(l.code)}
                  data-selected={data.language === l.code}
                  className="emerge-card emerge-option !px-5 !py-5"
                  dir={l.code === "ar" ? "rtl" : "ltr"}
                >
                  <span className="emerge-radio" aria-hidden="true" />
                  <span className="flex-1">
                    <span className="block text-lg font-semibold" style={l.code === "ar" ? { fontFamily: "'Noto Sans Arabic', sans-serif" } : undefined}>
                      {l.label}
                    </span>
                    <span className="block text-sm text-muted-foreground">{l.sub}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {current === "contact" && <ContactStep data={data} update={update} errors={errors} stepNumber={stepNumber} />}

        {current === "service" && (
          <div className="step-enter">
            <StepHeader number={stepNumber} title={t("step3.title")} subtitle={t("step3.subtitle")} />
            <OptionList
              name={t("step3.title")}
              options={SERVICE_OPTIONS}
              value={data.service}
              onChange={(v) => update({ service: v, assessment_type: v === "assessment" ? data.assessment_type : "" })}
              labelFn={(o) => t(`services.${o}`)}
              testIdPrefix="intake-service"
              otherValue={data.service_other}
              onOtherChange={(v) => update({ service_other: v })}
              otherPlaceholder={t("step3.otherPlaceholder")}
              otherError={errors.service_other}
            />
            {errors.service && <p className="field-error" role="alert">{errors.service}</p>}
          </div>
        )}

        {current === "assessment" && (
          <div className="step-enter">
            <StepHeader number={stepNumber} title={t("step4.title")} subtitle={t("step4.subtitle")} />
            <OptionList
              name={t("step4.title")}
              options={ASSESSMENT_OPTIONS}
              value={data.assessment_type}
              onChange={(v) => update({ assessment_type: v })}
              labelFn={(o) => t(`assessments.${o}`)}
              testIdPrefix="intake-assessment"
              otherValue={data.assessment_other}
              onOtherChange={(v) => update({ assessment_other: v })}
              otherPlaceholder={t("step4.otherPlaceholder")}
              otherError={errors.assessment_other}
            />
            {errors.assessment_type && <p className="field-error" role="alert">{errors.assessment_type}</p>}
          </div>
        )}

        {current === "referral" && (
          <div className="step-enter">
            <StepHeader number={stepNumber} title={t("step5.title")} subtitle={t("step5.subtitle")} />
            <OptionList
              name={t("step5.title")}
              options={REFERRAL_OPTIONS}
              value={data.referral_source}
              onChange={(v) => update({ referral_source: v })}
              labelFn={(o) => t(`referrals.${o}`)}
              testIdPrefix="intake-referral"
              otherValue={data.referral_other}
              onOtherChange={(v) => update({ referral_other: v })}
              otherPlaceholder={t("step5.otherPlaceholder")}
              otherError={errors.referral_other}
            />
            {errors.referral_source && <p className="field-error" role="alert">{errors.referral_source}</p>}
          </div>
        )}

        {current === "time" && (
          <div className="step-enter">
            <StepHeader number={stepNumber} title={t("step6.title")} subtitle={t("step6.subtitle")} />
            <OptionList
              name={t("step6.title")}
              options={TIME_OPTIONS_WITH_NONE}
              value={data.preferred_contact_time || "none"}
              onChange={(v) => update({ preferred_contact_time: v === "none" ? "" : v })}
              labelFn={(o) => (o === "none" ? t("step6.noPreference") : t(`contactTimes.${o}`))}
              testIdPrefix="intake-time"
            />
            {errors.preferred_contact_time && <p className="field-error" role="alert">{errors.preferred_contact_time}</p>}
            <div className="emerge-card mt-4 px-4 py-1 sm:px-5">
              <Field label={t("step6.specificTime")} htmlFor="preferred_time_specific" hint={t("step6.specificTimeHint")} error={errors.preferred_time_specific}>
                <input
                  id="preferred_time_specific"
                  data-testid="intake-specific-time-input"
                  type="time"
                  className="emerge-input"
                  style={{ direction: "ltr" }}
                  value={data.preferred_time_specific || ""}
                  onChange={(e) => update({ preferred_time_specific: e.target.value })}
                  aria-invalid={!!errors.preferred_time_specific}
                />
              </Field>
            </div>
          </div>
        )}

        {current === "assign" && (
          <SupervisorAssignStep
            stepNumber={stepNumber}
            service={data.service}
            value={booking}
            onChange={(patch) => setBooking((b) => ({ ...b, ...patch }))}
          />
        )}

        {current === "done" && (
          <div className="step-enter text-center" data-testid="intake-confirmation">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[hsl(var(--app-leaf))]/12">
              <CheckCircle2 className="h-9 w-9 text-[hsl(var(--app-leaf))]" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-semibold text-foreground">{t("step7.title")}</h1>
            <p className="mx-auto mt-3 max-w-md text-[16px] leading-relaxed text-muted-foreground">{t("step7.body")}</p>
            <div className="emerge-card mx-auto mt-6 max-w-md px-5 py-5">
              <p className="font-heading text-lg font-semibold text-foreground">EMERGE Therapy Center</p>
              <p className="mt-1 text-sm font-medium tracking-wide text-[hsl(var(--app-teal))]">Hope | Recovery | Healing | Resilience</p>
              {result?.id && (
                <p className="mt-4 text-xs text-muted-foreground">
                  {t("step7.reference")}: <span className="font-mono" data-testid="intake-reference-id">{result.id.slice(0, 8).toUpperCase()}</span>
                </p>
              )}
            </div>
            <button type="button" onClick={startOver} data-testid="intake-new-request-button" className="mt-6 text-sm font-semibold text-primary hover:underline">
              {t("step7.newRequest")}
            </button>
          </div>
        )}
      </main>

      {/* Sticky footer nav */}
      {current !== "language" && current !== "done" && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/85">
          <div className="mx-auto flex w-full max-w-[560px] items-center gap-3 px-4 py-3">
            <button type="button" data-testid="intake-back-button" onClick={goBack} className="inline-flex h-12 items-center justify-center gap-2 rounded-[10px] border border-border bg-white px-4 text-base font-medium text-foreground hover:bg-secondary">
              <BackIcon className="h-4 w-4" aria-hidden="true" />
              {t("common.back")}
            </button>
            <button
              type="button"
              data-testid={stepIndex === totalSteps - 1 ? "intake-submit-button" : "intake-next-button"}
              onClick={goNext}
              disabled={submitting}
              className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-[10px] bg-primary px-5 text-base font-semibold text-white hover:bg-[hsl(var(--app-navy-deep))] disabled:opacity-60"
              style={{ transition: "background-color 180ms" }}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> {t("common.submitting")}
                </>
              ) : stepIndex === totalSteps - 1 ? (
                t("common.submit")
              ) : (
                <>
                  {t("common.next")} <NextIcon className="h-4 w-4" aria-hidden="true" />
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function IntakePage() {
  const draft = loadDraft();
  const initial = draft?.data?.language && LOCALES[draft.data.language] ? draft.data.language : "en";
  return (
    <I18nProvider initial={initial}>
      <WizardInner />
    </I18nProvider>
  );
}

/** Same wizard, filled in by a supervisor, with a therapist-assignment step at the end. */
export function SupervisorIntakePage() {
  const draft = loadDraft(SUPERVISOR_DRAFT_KEY);
  const initial = draft?.data?.language && LOCALES[draft.data.language] ? draft.data.language : "en";
  return (
    <I18nProvider initial={initial}>
      <WizardInner supervisorMode />
    </I18nProvider>
  );
}
